import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Parent as BaseParent, Student } from '@/interface/types/user'
import { useState, useEffect } from 'react'
import FileUploader from '@/components/shared/FileUploader'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import { Eye, EyeOff, Search, X, Check, ChevronsUpDown } from 'lucide-react'
import { FormYearMonthPicker } from '@/components/ui/form-year-month-picker'
import { useTranslation } from 'react-i18next'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

// Helper function to get all nationality keys
const getAllNationalityKeys = (): string[] => {
  return [
    'afghan', 'albanian', 'algerian', 'american', 'andorran', 'angolan', 'antiguans',
    'argentine', 'armenian', 'australian', 'austrian', 'azerbaijani', 'bahamian',
    'bahraini', 'bangladeshi', 'barbadian', 'barbudans', 'batswana', 'belarusian',
    'belgian', 'belizean', 'beninese', 'bhutanese', 'bolivian', 'bosnian', 'brazilian',
    'british', 'bruneian', 'bulgarian', 'burkinabe', 'burmese', 'burundian', 'cambodian',
    'cameroonian', 'canadian', 'cape verdean', 'central african', 'chadian', 'chilean',
    'chinese', 'colombian', 'comoran', 'congolese', 'costa rican', 'croatian', 'cuban',
    'cypriot', 'czech', 'danish', 'djibouti', 'dominican', 'dutch', 'east timorese',
    'ecuadorean', 'egyptian', 'emirian', 'equatorial guinean', 'eritrean', 'estonian',
    'ethiopian', 'fijian', 'filipino', 'finnish', 'french', 'gabonese', 'gambian',
    'georgian', 'german', 'ghanaian', 'greek', 'grenadian', 'guatemalan', 'guinea-bissauan',
    'guinean', 'guyanese', 'haitian', 'herzegovinian', 'honduran', 'hungarian', 'icelander',
    'indian', 'indonesian', 'iranian', 'iraqi', 'irish', 'israeli', 'italian', 'ivorian',
    'jamaican', 'japanese', 'jordanian', 'kazakhstani', 'kenyan', 'kittian and nevisian',
    'kuwaiti', 'kyrgyz', 'laotian', 'latvian', 'lebanese', 'liberian', 'libyan',
    'liechtensteiner', 'lithuanian', 'luxembourger', 'macedonian', 'malagasy', 'malawian',
    'malaysian', 'maldivan', 'malian', 'maltese', 'marshallese', 'mauritanian', 'mauritian',
    'mexican', 'micronesian', 'moldovan', 'monacan', 'mongolian', 'moroccan', 'mosotho',
    'motswana', 'mozambican', 'namibian', 'nauruan', 'nepalese', 'new zealander', 'ni-vanuatu',
    'nicaraguan', 'nigerien', 'north korean', 'northern irish', 'norwegian', 'omani',
    'pakistani', 'palauan', 'panamanian', 'papua new guinean', 'paraguayan', 'peruvian',
    'polish', 'portuguese', 'qatari', 'romanian', 'russian', 'rwandan', 'saint lucian',
    'salvadoran', 'samoan', 'san marinese', 'sao tomean', 'saudi', 'scottish', 'senegalese',
    'serbian', 'seychellois', 'sierra leonean', 'singaporean', 'slovakian', 'slovenian',
    'solomon islander', 'somali', 'south african', 'south korean', 'spanish', 'sri lankan',
    'sudanese', 'surinamer', 'swazi', 'swedish', 'swiss', 'syrian', 'taiwanese', 'tajik',
    'tanzanian', 'thai', 'togolese', 'tongan', 'trinidadian or tobagonian', 'tunisian',
    'turkish', 'tuvaluan', 'ugandan', 'ukrainian', 'uruguayan', 'uzbekistani', 'venezuelan',
    'vietnamese', 'welsh', 'yemenite', 'zambian', 'zimbabwean'
  ]
}

// Add missing properties to Parent interface
interface Parent extends BaseParent {
  avatar?: string
  isActive?: boolean
}

const parentSchema = z.object({
  cin: z.string().min(8, 'CIN must be 8 characters'),
  firstname: z.string().min(2, 'First name must be at least 2 characters'),
  lastname: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email format'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character'
    )
    .optional(),
  birthday: z.date().optional(),
  gender: z.enum(['male', 'female'] as const),
  address: z.string().optional(),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  avatar: z.string().url().default(DEFAULT_AVATAR),
  isActive: z.boolean().default(true),
  studentIds: z.array(z.string()).optional(),
})

type ParentFormValues = z.infer<typeof parentSchema>
export type { ParentFormValues }

interface ParentFormProps {
  initialData?: Partial<Parent>
  students: Student[]
  onSubmit: (data: ParentFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
}

/**
 * A reusable form component for creating and editing parents
 * @param {ParentFormProps} props - The props for the ParentForm component
 * @returns {JSX.Element} The rendered ParentForm component
 */
export function ParentForm({
  initialData,
  students,
  onSubmit,
  onCancel,
  submitLabel,
}: ParentFormProps) {
  const { t } = useTranslation('admin', { keyPrefix: 'parent-folder.parentForm' })
  const { t: tNationalities } = useTranslation('nationalities')
  // Get the initial selected student IDs from the parent's students
  const initialStudentIds =
    initialData?.students?.map((student) => student.id) || []
  const [selectedStudentIds, setSelectedStudentIds] =
    useState<string[]>(initialStudentIds)

  // State for student search
  const [studentSearchQuery, setStudentSearchQuery] = useState('')

  // Filter students based on search query
  const filteredStudents = students.filter((student) => {
    const fullName = `${student.firstname} ${student.lastname}`.toLowerCase()
    const cin = student.cin?.toLowerCase() || ''
    const query = studentSearchQuery.toLowerCase()
    return fullName.includes(query) || cin.includes(query)
  })

  // State for password visibility toggle
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  // Helper function to normalize nationality key (lowercase)
  const normalizeNationalityKey = (nationality: string | undefined): string => {
    if (!nationality) return 'tunisian'
    return nationality.toLowerCase()
  }

  // Nationality search state - initialize with initialData if available
  const [nationalitySearchOpen, setNationalitySearchOpen] = useState(false)
  const [nationalitySearchQuery, setNationalitySearchQuery] = useState('')
  const [selectedNationality, setSelectedNationality] = useState<string>(
    normalizeNationalityKey(initialData?.nationality)
  )

  // Function to toggle password visibility
  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev)
  }

  // Normalize the gender value from server
  const normalizeGender = (gender: string | undefined): 'male' | 'female' => {
    if (!gender) return 'male' // Default to male if no gender is provided
    return gender.toLowerCase() as 'male' | 'female'
  }

  // Log initial data for debugging
  // console.log('ParentForm initialData:', initialData)
  // console.log('ParentForm initialStudentIds:', initialStudentIds)
  // console.log('ParentForm students:', students)

  // Calculate date 5 years ago for default and maxDate
  const getFiveYearsAgo = () => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 5)
    return date
  }
  // Set maxDate to 5 years ago, but allow existing birthdays that are less than 5 years ago
  // (for existing users who were created before this validation)
  const fiveYearsAgo = getFiveYearsAgo()
  const existingBirthday = initialData?.birthday ? new Date(initialData.birthday) : null
  const maxBirthdayDate = existingBirthday && existingBirthday > fiveYearsAgo 
    ? existingBirthday 
    : fiveYearsAgo

  const form = useForm({
    defaultValues: {
      cin: initialData?.cin || '',
      firstname: initialData?.firstname || '',
      lastname: initialData?.lastname || '',
      email: initialData?.email || '',
      password: '',
      birthday: initialData?.birthday
        ? new Date(initialData.birthday)
        : getFiveYearsAgo(),
      gender: normalizeGender(initialData?.gender),
      address: initialData?.address || '',
      phone: initialData?.phone || '',
      nationality: normalizeNationalityKey(initialData?.nationality),
      avatar: initialData?.avatar || DEFAULT_AVATAR,
      isActive:
        initialData?.isActive !== undefined ? initialData.isActive : true,
      studentIds: initialStudentIds,
    },
    onSubmit: async ({ value }) => {
      // Update the studentIds field with the selected students
      const formData = {
        ...value,
        studentIds: selectedStudentIds,
        // Ensure avatar and isActive are always present
        avatar: value.avatar || DEFAULT_AVATAR,
        isActive: value.isActive !== undefined ? value.isActive : true,
        // Use selected nationality if available
        nationality: selectedNationality || value.nationality || 'tunisian',
      }
      await onSubmit(formData)
    },
  })

  // Sync selected nationality when initialData changes (after form is created)
  useEffect(() => {
    const nationality = normalizeNationalityKey(initialData?.nationality)
    setSelectedNationality(nationality)
    // Also update the form field value
    form.setFieldValue('nationality', nationality)
  }, [initialData?.nationality, form])
  
  // Sync field value when selectedNationality changes
  useEffect(() => {
    if (selectedNationality) {
      form.setFieldValue('nationality', selectedNationality)
    }
  }, [selectedNationality, form])

  // Function to handle student selection
  const handleStudentSelect = (studentId: string) => {
    setSelectedStudentIds((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId)
      }
      return [...prev, studentId]
    })
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="cin"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('fields.cin.validation.required')
              if (value.length !== 8) return t('fields.cin.validation.length')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="cin">{t('fields.cin.label')}</Label>
              <Input
                id="cin"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.cin.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="firstname"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('fields.firstname.validation.required')
              if (value.length < 2)
                return t('fields.firstname.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="firstname">{t('fields.firstname.label')}</Label>
              <Input
                id="firstname"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.firstname.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="lastname"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('fields.lastname.validation.required')
              if (value.length < 2)
                return t('fields.lastname.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="lastname">{t('fields.lastname.label')}</Label>
              <Input
                id="lastname"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.lastname.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('fields.email.validation.required')
              if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                return t('fields.email.validation.format')
              }
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="email">{t('fields.email.label')}</Label>
              <Input
                id="email"
                type="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.email.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {!initialData ? (
          <form.Field
            name="password"
            validators={{
              onChange: ({ value }) => {
                if (!value) return t('fields.password.validation.required')
                return undefined
              },
              onBlur: ({ value }) => {
                if (!value) return t('fields.password.validation.required')
                const errors = []
                if (value.length < 6) {
                  errors.push(t('fields.password.validation.minLength'))
                }
                if (!/[A-Z]/.test(value)) {
                  errors.push(t('fields.password.validation.uppercase'))
                }
                if (!/[0-9]/.test(value)) {
                  errors.push(t('fields.password.validation.number'))
                }
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                  errors.push(t('fields.password.validation.special'))
                }
                return errors.length > 0 ? errors.join(', ') : undefined
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="password">{t('fields.password.label')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    value={field.state.value}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => {
                      field.handleBlur()
                      setIsPasswordFocused(false)
                    }}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('fields.password.placeholder')}
                    className={
                      field.state.meta.isTouched && field.state.meta.errors
                        ? 'border-red-500'
                        : ''
                    }
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center"
                    tabIndex={-1}
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {/* Password requirements - only show when field is focused or has content */}
                {(isPasswordFocused || field.state.value) && (
                  <div className="mt-2 text-xs text-gray-500">
                    <p>{t('fields.password.strength.requirements.title')}</p>
                    <ul className="list-disc pl-5 space-y-1 mt-1">
                      <li className={field.state.value && field.state.value.length >= 6 ? 'text-green-500' : ''}>
                        {t('fields.password.strength.requirements.length')}
                      </li>
                      <li className={field.state.value && /[A-Z]/.test(field.state.value) ? 'text-green-500' : ''}>
                        {t('fields.password.strength.requirements.uppercase')}
                      </li>
                      <li className={field.state.value && /[0-9]/.test(field.state.value) ? 'text-green-500' : ''}>
                        {t('fields.password.strength.requirements.number')}
                      </li>
                      <li className={field.state.value && /[!@#$%^&*(),.?":{}|<>]/.test(field.state.value) ? 'text-green-500' : ''}>
                        {t('fields.password.strength.requirements.special')}
                      </li>
                    </ul>
                  </div>
                )}
                {/* Password strength indicator */}
                {field.state.value && (
                  <div className="mt-2">
                    <div className="flex justify-between mb-1">
                      <span className="text-xs">{t('fields.password.strength.title')}</span>
                      <span className="text-xs">
                        {(() => {
                          const value = field.state.value
                          const hasLength = value.length >= 6
                          const hasUpper = /[A-Z]/.test(value)
                          const hasNumber = /[0-9]/.test(value)
                          const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value)
                          const score = [hasLength, hasUpper, hasNumber, hasSpecial].filter(Boolean).length
                          if (score === 4) return t('fields.password.strength.strong')
                          if (score === 3) return t('fields.password.strength.good')
                          if (score === 2) return t('fields.password.strength.fair')
                          return t('fields.password.strength.weak')
                        })()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className={`h-1 rounded-full ${(() => {
                          const value = field.state.value
                          const hasLength = value.length >= 6
                          const hasUpper = /[A-Z]/.test(value)
                          const hasNumber = /[0-9]/.test(value)
                          const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value)
                          const score = [hasLength, hasUpper, hasNumber, hasSpecial].filter(Boolean).length
                          if (score === 4) return 'bg-green-500 w-full'
                          if (score === 3) return 'bg-blue-500 w-3/4'
                          if (score === 2) return 'bg-yellow-500 w-2/4'
                          return 'bg-red-500 w-1/4'
                        })()}`}
                      ></div>
                    </div>
                  </div>
                )}
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        ) : (
          <form.Field
            name="password"
            validators={{
              onBlur: ({ value }) => {
                if (!value || value.trim() === '') return undefined
                const errors = []
                if (value.length < 6) {
                  errors.push(t('fields.password.validation.minLength'))
                }
                if (!/[A-Z]/.test(value)) {
                  errors.push(t('fields.password.validation.uppercase'))
                }
                if (!/[0-9]/.test(value)) {
                  errors.push(t('fields.password.validation.number'))
                }
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                  errors.push(t('fields.password.validation.special'))
                }
                return errors.length > 0 ? errors.join(', ') : undefined
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="password">
                  {t('fields.password.label')} (leave empty to keep current)
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    value={field.state.value}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => {
                      field.handleBlur()
                      setIsPasswordFocused(false)
                    }}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('fields.password.placeholder')}
                    className={
                      field.state.meta.isTouched && field.state.meta.errors
                        ? 'border-red-500'
                        : ''
                    }
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center"
                    tabIndex={-1}
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
                {isPasswordFocused && (
                  <p className="text-xs text-gray-500">
                    {t('fields.password.label')} (leave empty to keep current)
                  </p>
                )}
              </div>
            )}
          </form.Field>
        )}

        <form.Field 
          name="birthday"
          validators={{
            onChange: ({ value }) => {
              if (!value) return undefined // Optional field
              
              const today = new Date()
              const birthDate = new Date(value)
              let age = today.getFullYear() - birthDate.getFullYear()
              const monthDiff = today.getMonth() - birthDate.getMonth()
              
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--
              }
              
              if (age < 5) {
                return t('fields.birthday.validation.minAge')
              }
              
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <FormYearMonthPicker
                id="birthday"
                label={t('fields.birthday.label')}
                value={field.state.value}
                onChange={(date) => field.handleChange(date || getFiveYearsAgo())}
                onBlur={field.handleBlur}
                placeholder={t('fields.birthday.placeholder')}
                maxDate={maxBirthdayDate}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="gender"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('fields.gender.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="gender">{t('fields.gender.label')}</Label>
              <Select
                value={field.state.value}
                onValueChange={(value: 'male' | 'female') =>
                  field.handleChange(value)
                }
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder={t('fields.gender.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('fields.gender.options.male')}</SelectItem>
                  <SelectItem value="female">{t('fields.gender.options.female')}</SelectItem>
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="address">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="address">{t('fields.address.label')}</Label>
              <Input
                id="address"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.address.placeholder')}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="phone">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="phone">{t('fields.phone.label')}</Label>
              <Input
                id="phone"
                type="tel"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('fields.phone.placeholder')}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="nationality">
          {(field) => {
            // Use selectedNationality as primary source, fallback to field value, then initialData, then default
            // Normalize all values to lowercase for comparison
            const normalizedFieldValue = field.state.value ? normalizeNationalityKey(field.state.value) : null
            const normalizedInitialData = initialData?.nationality ? normalizeNationalityKey(initialData.nationality) : null
            const currentNationality = selectedNationality || normalizedFieldValue || normalizedInitialData || 'tunisian'
            
            return (
              <div className="space-y-2">
                <Label htmlFor="nationality">
                  {t('fields.nationality.label')}
                </Label>
                <Popover open={nationalitySearchOpen} onOpenChange={setNationalitySearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={nationalitySearchOpen}
                      className="w-full justify-between"
                      type="button"
                    >
                      {(() => {
                        if (getAllNationalityKeys().includes(currentNationality)) {
                          return tNationalities(`nationalities.${currentNationality}`, currentNationality)
                        }
                        return t('fields.nationality.placeholder')
                      })()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <div className="p-2">
                      <Input
                        placeholder={t('fields.nationality.placeholder')}
                        value={nationalitySearchQuery}
                        onChange={(e) => setNationalitySearchQuery(e.target.value)}
                        className="mb-2"
                      />
                    </div>
                    <ScrollArea className="h-[200px]">
                      <div className="p-1">
                        {getAllNationalityKeys()
                          .filter((key) => {
                            if (!nationalitySearchQuery) return true
                            const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                            const searchLower = nationalitySearchQuery.toLowerCase()
                            return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                          })
                          .map((key) => {
                            const translated = tNationalities(`nationalities.${key}`, key)
                            const isSelected = currentNationality === key
                            return (
                              <button
                                key={key}
                                type="button"
                                className={cn(
                                  'w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                                  isSelected && 'bg-accent'
                                )}
                                onClick={() => {
                                  setSelectedNationality(key)
                                  field.handleChange(key)
                                  setNationalitySearchQuery('')
                                  setNationalitySearchOpen(false)
                                }}
                              >
                                <span>{translated}</span>
                                {isSelected && <Check className="h-4 w-4" />}
                              </button>
                            )
                          })}
                        {nationalitySearchQuery && getAllNationalityKeys().filter((key) => {
                          const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                          const searchLower = nationalitySearchQuery.toLowerCase()
                          return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                        }).length === 0 && (
                          <div className="px-2 py-1.5 text-sm text-gray-500">
                            No nationality found
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>
            )
          }}
        </form.Field>

        <form.Field name="avatar">
          {(field) => (
            <FileUploader
              label={t('fields.avatar.label')}
              defaultPreview={field.state.value || DEFAULT_AVATAR}
              onFileUploaded={(url) => field.handleChange(url)}
              isAvatar={true}
              previewComponent={(previewUrl) => (
                <CdnImage
                  src={previewUrl}
                  alt={t('fields.avatar.label')}
                  className="h-28 w-28 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                />
              )}
            />
          )}
        </form.Field>

        <form.Field name="isActive">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="isActive">{t('fields.isActive.label')}</Label>
              <Select
                value={field.state.value.toString()}
                onValueChange={(value) => field.handleChange(value === 'true')}
              >
                <SelectTrigger id="isActive">
                  <SelectValue placeholder={t('fields.isActive.label')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t('fields.isActive.options.active')}</SelectItem>
                  <SelectItem value="false">{t('fields.isActive.options.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </form.Field>
      </div>

      <div className="space-y-2">
        <Label htmlFor="students">{t('fields.students.label')}</Label>
        <div className="mb-2 relative">
          <Input
            placeholder={t('fields.students.search.placeholder')}
            value={studentSearchQuery}
            onChange={(e) => setStudentSearchQuery(e.target.value)}
            className="w-full pl-9"
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          {studentSearchQuery && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setStudentSearchQuery('')}
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
        <div className="border rounded-md p-2">
          <ScrollArea className="h-[200px] w-full">
            <div className="space-y-2">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                    onClick={() => handleStudentSelect(student.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={() => handleStudentSelect(student.id)}
                      className="h-4 w-4"
                    />
                    <span>
                      {student.firstname} {student.lastname} ({student.cin})
                    </span>
                  </div>
                ))
              ) : (
                <div className="p-2 text-center text-gray-500">
                  {studentSearchQuery
                    ? t('fields.students.search.noResults')
                    : t('fields.students.search.noStudents')}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedStudentIds.map((id) => {
            const student = students.find((s) => s.id === id)
            return (
              <Badge
                key={id}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {student?.firstname} {student?.lastname}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleStudentSelect(id)
                  }}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )
          })}
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('buttons.cancel')}
        </Button>
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  )
}
